/**
 * Budowanie znaczników meta dla stron indeksowanych przez wyszukiwarki.
 *
 * Umowa 1/08/2026, Załącznik nr 1 poz. 2, 3 i 6. Szablony tytułów są przepisane
 * z kolumny „Zakres wykonania" dosłownie — nie improwizujemy, bo odbiór sprawdza
 * zgodność z nimi.
 *
 * Funkcje są czyste i nie zależą od Reacta ani od Expressa: te same wywołania
 * dają ten sam wynik na serwerze i w przeglądarce. To warunek konieczny, żeby
 * podłączenie Reacta nie zgłaszało rozjazdu.
 */

/** Dolna granica długości opisu dla stron dzieł — wymóg kryterium odbioru poz. 2. */
export const artworkDescriptionMinLength: number = 150;

/** Górna granica długości opisu dla stron dzieł — wymóg kryterium odbioru poz. 2. */
export const artworkDescriptionMaxLength: number = 160;

export interface SeoMeta {
  title: string;
  description: string;
  /** Adres bezwzględny, zawsze bez parametrów zapytania — poz. 3. */
  canonical: string;
  ogType: string;
  ogImage: string | null;
  ogImageAlt: string | null;
}

/**
 * Zabezpiecza wartość wstawianą do atrybutu HTML.
 *
 * To nie jest ostrożność teoretyczna: 141 dzieł w bazie produkcyjnej ma cudzysłów
 * w nazwie. Bez tej funkcji pierwsze takie dzieło rozerwałoby atrybut `content`
 * i wszystko za nim wylądowałoby w treści strony jako tekst.
 */
export function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Sprowadza tekst redakcyjny do jednej linii bez znaczników.
 *
 * Opisy dzieł wpisują artyści przez edytor, więc bywa w nich HTML, encje i twarde
 * spacje. W znaczniku meta to śmieci — Google i tak pokaże tekst, nie kod.
 */
export function toPlainText(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Przycina tekst do zadanego przedziału długości.
 *
 * Kryterium odbioru poz. 2 mówi o przedziale 150–160 znaków, więc samo „nie dłużej
 * niż 160" nie wystarcza. Cięcie idzie po granicy słowa, ale tylko wtedy, gdy wynik
 * nie spadnie poniżej dolnej granicy — inaczej jedno długie słowo potrafiłoby zbić
 * opis do stu znaków i wywrócić kryterium.
 */
export function clampToRange(value: string, minLength: number, maxLength: number): string {
  const normalized: string = value.replace(/\s+/g, ' ').trim();

  if (normalized.length <= maxLength) {
    return normalized;
  }

  const head: string = normalized.slice(0, maxLength);
  const lastSpace: number = head.lastIndexOf(' ');

  if (lastSpace >= minLength) {
    const atWordBoundary: string = head.slice(0, lastSpace);
    const withoutTrailingPunctuation: string = atWordBoundary.replace(/[\s,;:–—-]+$/, '');

    // Oczyszczenie z końcowej interpunkcji stosujemy tylko wtedy, gdy nie zbija
    // wyniku poniżej dolnej granicy. Kryterium odbioru mierzy długość, nie estetykę,
    // a opis kończący się przecinkiem jest mniej kosztowny niż opis o znak za krótki.
    return withoutTrailingPunctuation.length >= minLength ? withoutTrailingPunctuation : atWordBoundary;
  }

  return `${normalized.slice(0, maxLength - 1).trimEnd()}…`;
}

/**
 * Skleja fragmenty opisu, pomijając puste.
 *
 * Fragmenty przychodzą z pól, które bywają puste — 914 dzieł na 2968 ma opis krótszy
 * niż 150 znaków, a 2 nie mają go wcale. Dlatego opis powstaje ze złożenia wielu
 * źródeł, a nie ze skrócenia jednego pola.
 */
function joinFragments(fragments: (string | null | undefined)[], separator: string = ' '): string {
  return fragments
    .map((fragment: string | null | undefined): string => (fragment ?? '').trim())
    .filter((fragment: string): boolean => fragment.length > 0)
    .join(separator);
}

export interface ArtworkMetaSource {
  itemName: string;
  artistName: string;
  categoryLabel: string | null;
  /**
   * Technika w brzmieniu dla czytelnika („Akryl, technika mieszana"). Na stronie dzieła
   * składana z atrybutu `technique`; na listingach API daje tylko `label` (medium).
   */
  techniqueLabel: string | null;
  year: string | null;
  description: string | null;
  photoUrl: string | null;
  titleSuffix?: string | null; // dopisek przy dziełach o tej samej nazwie, patrz artworkTitleSuffix.utils.ts
}

/**
 * Tytuł strony dzieła — szablon z Załącznika nr 1 poz. 2.
 *
 * Osobne dzieła tego samego artysty o tej samej nazwie dostają dopisek w nawiasie, bo szablon
 * dałby im ten sam tytuł, a umowa wymaga unikalnego. Kilka aukcji JEDNEGO dzieła zostaje przy
 * wspólnym tytule, bo to wciąż jedno dzieło. Pozostałe strony mają dokładnie szablon z umowy.
 */
export function buildArtworkTitle(source: Pick<ArtworkMetaSource, 'itemName' | 'artistName' | 'titleSuffix'>): string {
  return `${source.itemName} – ${source.artistName}${source.titleSuffix ? ` (${source.titleSuffix})` : ''} | on*arte`;
}

/**
 * Treść `<h1>` strony dzieła: „[Nazwa dzieła] – [Artysta]".
 *
 * Ten sam szablon co tytuł z poz. 2, tylko bez marki — tytuł karty przeglądarki może
 * kończyć się na „| on*arte", widoczny nagłówek nie. Wspólna funkcja, bo tytuł składa
 * i serwer, i przeglądarka, a nagłówek widok — trzy miejsca nie mają prawa się rozjechać.
 */
export function buildArtworkHeading(source: Pick<ArtworkMetaSource, 'itemName' | 'artistName'>): string {
  return `${source.itemName} – ${source.artistName}`;
}

export function buildArtistTitle(profileName: string): string {
  return `${profileName} – Prace artysty | on*arte`;
}

/**
 * Tytuł strony, która nie ma własnego szablonu z umowy: nazwa plus marka.
 *
 * Jedno miejsce dla serwera i przeglądarki. Wcześniej `AppWrapper` składał tytuł sam
 * (`${t(route.title)} | on*arte`), a serwer oddawał samą nazwę trasy — robot dostawał
 * inny tytuł niż użytkownik, na 21 adresach z map witryny. Pusta nazwa daje samą markę,
 * zamiast wiszącego separatora.
 */
export function buildRouteTitle(title: string): string {
  return title.trim() ? `${title.trim()} | on*arte` : 'on*arte';
}

/**
 * Litera przedziału alfabetu z adresu `?query=Z` — /artysci przenosi tak na listę
 * alfabetyczną. Jedno miejsce dla serwera i przeglądarki: obie strony muszą wyliczyć
 * tę samą literę, bo wchodzi ona do klucza pamięci podręcznej zasiewu. Wartość dłuższa
 * niż jeden znak to nie litera przedziału — wtedy przedział domyślny, czyli pusty ciąg.
 */
export function normalizeAlphabetLetter(query: string | null | undefined): string {
  return query?.length === 1 ? query : '';
}

export const homeTitle: string = 'on*arte – Platforma sztuki współczesnej | Kup, sprzedaj, odkryj';

/**
 * Treść jedynego <h1> strony głównej — poz. 5: „opisujący platformę".
 *
 * Nagłówek jest widoczny: stoi jako podpis pod znakiem firmowym w nagłówku strony.
 * Nie powtarza w treści nazwy „on*arte", bo ta jest tuż nad nim, w samym znaku
 * (i w jego atrybucie alt) — powtórzenie czytałoby się jak zająknięcie.
 */
export const homeHeading: string = 'platforma sztuki współczesnej';

/**
 * Opis strony dzieła, gwarantowane 150–160 znaków.
 *
 * Kolejność jest celowa: najpierw fakty z bazy (nazwa, artysta, kategoria, rok),
 * potem tekst od artysty, na końcu stałe zdanie o serwisie. Dzięki temu opis dzieła
 * bez własnego tekstu nadal niesie konkret, a nie samą nazwę marki.
 */
export function buildArtworkDescription(source: ArtworkMetaSource): string {
  const facts: string = joinFragments([
    `${source.itemName} –`,
    `${source.artistName}${source.titleSuffix ? ` (${source.titleSuffix})` : ''}.`,
    source.categoryLabel ? `${source.categoryLabel}` : null,
    source.year ? `z ${source.year} roku.` : (source.categoryLabel ? '.' : null),
  ]).replace(' .', '.');

  return padToMinLength(
    joinFragments([facts, toPlainText(source.description)]),
    artworkTailSentences
  );
}

/**
 * Zdania dopełniające, dokładane po kolei, dopóki opis nie osiągnie dolnej granicy.
 *
 * Potrzebne, bo samo przycinanie nie wystarcza: dzieło o krótkiej nazwie i bez opisu
 * dawało 135 znaków, czyli poniżej wymaganych 150. Zdania są uporządkowane od
 * najbardziej konkretnego do najogólniejszego, żeby dopełnienie sięgało po ogólniki
 * dopiero wtedy, gdy naprawdę brakuje treści.
 */
const artworkTailSentences: string[] = [
  'Zobacz zdjęcia, wymiary i cenę na on*arte – platformie sztuki współczesnej.',
  'Kupuj i sprzedawaj dzieła bezpośrednio od artystów oraz kolekcjonerów.',
  'Sprawdź szczegóły oferty i dostępność pracy.',
];

/**
 * Dokłada zdania, aż tekst zmieści się w wymaganym przedziale.
 *
 * Kolejność operacji ma znaczenie: najpierw dopełniamy do dolnej granicy, dopiero
 * potem przycinamy do górnej. Odwrotnie wynik potrafiłby wypaść poniżej minimum.
 */
function padToMinLength(text: string, fillers: string[]): string {
  let result: string = text.replace(/\s+/g, ' ').trim();

  for (const filler of fillers) {
    if (result.length >= artworkDescriptionMinLength) {
      break;
    }
    result = joinFragments([result, filler]);
  }

  return clampToRange(result, artworkDescriptionMinLength, artworkDescriptionMaxLength);
}

/**
 * Opis profilu artysty.
 *
 * Umowa nie narzuca tu przedziału długości — kryterium poz. 2 wymaga przedziału
 * 150–160 znaków wyłącznie dla stron dzieł. Trzymamy jednak ten sam limit górny,
 * bo dłuższe opisy wyszukiwarka i tak ucina.
 */
export function buildArtistDescription(profileName: string, bio: string | null, description: string | null): string {
  const authorText: string = joinFragments([toPlainText(bio), toPlainText(description)]);

  return padToMinLength(
    joinFragments([`${profileName} – prace artysty.`, authorText]),
    [
      `Poznaj sylwetkę i zobacz prace artysty ${profileName} na on*arte.`,
      'Platforma sztuki współczesnej: kupuj i sprzedawaj dzieła bezpośrednio od twórców.',
      'Sprawdź dostępne prace i szczegóły ofert.',
    ]
  );
}

export const homeDescription: string = clampToRange(
  'on*arte to platforma sztuki współczesnej. Kupuj i sprzedawaj obrazy, rzeźby, '
  + 'grafikę i fotografię prosto od artystów oraz kolekcjonerów. Odkryj nowe prace.',
  artworkDescriptionMinLength,
  artworkDescriptionMaxLength
);

/**
 * Tekst alternatywny zdjęcia dzieła — szablon z poz. 7:
 * „[Nazwa dzieła] – [Artysta], [technika], [rok]".
 *
 * Ten sam ciąg idzie do `alt` obrazków i do `og:image:alt`. Gdy dzieło nie ma
 * techniki, wchodzi kategoria (Malarstwo, Rzeźba) — lepsza przybliżona technika
 * niż dziura w szablonie. Brakujący rok po prostu pomijamy.
 */
export function buildArtworkImageAlt(
  source: Pick<ArtworkMetaSource, 'itemName' | 'artistName' | 'categoryLabel' | 'techniqueLabel' | 'year'>
): string {
  const technique: string | null = source.techniqueLabel ?? source.categoryLabel;

  return [
    `${source.itemName} – ${source.artistName}`,
    // Techniki bywają dwie („Akryl, Technika własna") — każda z małej litery, bo w zdaniu.
    technique ? technique.split(', ').map(lowercaseFirst).join(', ') : null,
    source.year,
  ]
    .filter((fragment: string | null): boolean => !!fragment)
    .join(', ');
}

function lowercaseFirst(value: string): string {
  return value.charAt(0).toLowerCase() + value.slice(1);
}

/**
 * Etykieta techniki z wartości, które wpisują użytkownicy — odrzuca wypełniacze
 * w rodzaju „-" czy „❋❋❋" (takie naprawdę siedzą w polu medium w bazie).
 */
export function cleanTechniqueLabel(value: string | null | undefined): string | null {
  const text: string = toPlainText(value);

  // Litery łacińskie z ogonkami; bez `\p{L}`, bo cel kompilacji to ES6.
  return /[a-zÀ-ɏ]/i.test(text) ? text : null;
}

/**
 * Obraz udostępniania dla stron bez własnego zdjęcia (główna, listingi, statyczne,
 * artyści bez awatara). Kryterium poz. 6 wymaga, by podgląd w walidatorze zawierał
 * obraz — bez wartości domyślnej połowa próby by go nie miała.
 * Źródło wektorowe leży obok: `public/images/share-default.svg`.
 */
export const defaultShareImagePath: string = '/images/share-default.png';

/**
 * Znaczniki dla strony, która nie ma własnych danych do opisania — listingu,
 * strony statycznej, formularza. Tytuł i opis pochodzą z definicji trasy, canonical
 * z adresu żądania (bez parametrów). Bez tego takie strony nie miały canonicala
 * ani znaczników udostępniania, a próba odbiorowa poz. 3 i 6 losuje spośród
 * WSZYSTKICH publicznych podstron, nie tylko dzieł i artystów.
 */
export function buildGenericMeta(
  baseUrl: string,
  pathname: string,
  title: string,
  description: string
): SeoMeta {
  return {
    title: buildRouteTitle(title),
    description,
    canonical: buildCanonicalUrl(baseUrl, pathname),
    ogType: 'website',
    ogImage: `${baseUrl}${defaultShareImagePath}`,
    ogImageAlt: 'on*arte – platforma sztuki współczesnej',
  };
}

/** Znaczniki strony głównej — tytuł z szablonu umowy (poz. 2), bez doklejanej marki trasy. */
export function buildHomeMeta(baseUrl: string): SeoMeta {
  return {
    title: homeTitle,
    description: homeDescription,
    canonical: buildCanonicalUrl(baseUrl, '/'),
    ogType: 'website',
    ogImage: `${baseUrl}${defaultShareImagePath}`,
    ogImageAlt: 'on*arte – platforma sztuki współczesnej',
  };
}

/**
 * Składa adres bezwzględny z adresu bazowego i ścieżki.
 *
 * Link kanoniczny musi być bezwzględny i pozbawiony parametrów zapytania (poz. 3).
 * Parametry obcinamy tutaj, a nie u wywołującego, żeby nie dało się o tym zapomnieć
 * w jednym z czterech miejsc.
 */
export function buildCanonicalUrl(baseUrl: string, pathname: string): string {
  const normalizedBase: string = baseUrl.replace(/\/+$/, '');
  const pathOnly: string = pathname.split(/[?#]/)[0] ?? '/';
  const normalizedPath: string = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
  const withoutTrailingSlash: string = normalizedPath.length > 1
    ? normalizedPath.replace(/\/+$/, '')
    : normalizedPath;

  return `${normalizedBase}${withoutTrailingSlash}`;
}

/**
 * Zamienia obiekt znaczników na fragment HTML wstawiany do sekcji head.
 *
 * Wszystkie wartości przechodzą przez `escapeHtmlAttribute`. Znacznik `og:image`
 * pomijamy w całości, gdy dzieło nie ma zdjęcia — pusty `content` jest gorszy niż
 * brak znacznika, bo walidatory udostępniania zgłaszają go jako błąd.
 */
export function renderMetaTags(meta: SeoMeta): string {
  const tags: string[] = [
    `<link rel="canonical" href="${escapeHtmlAttribute(meta.canonical)}" />`,
    `<meta property="og:title" content="${escapeHtmlAttribute(meta.title)}" />`,
    `<meta property="og:description" content="${escapeHtmlAttribute(meta.description)}" />`,
    `<meta property="og:type" content="${escapeHtmlAttribute(meta.ogType)}" />`,
    `<meta property="og:url" content="${escapeHtmlAttribute(meta.canonical)}" />`,
    '<meta property="og:site_name" content="on*arte" />',
    '<meta property="og:locale" content="pl_PL" />',
    `<meta name="twitter:card" content="${meta.ogImage ? 'summary_large_image' : 'summary'}" />`,
    `<meta name="twitter:title" content="${escapeHtmlAttribute(meta.title)}" />`,
    `<meta name="twitter:description" content="${escapeHtmlAttribute(meta.description)}" />`,
  ];

  if (meta.ogImage) {
    tags.push(`<meta property="og:image" content="${escapeHtmlAttribute(meta.ogImage)}" />`);
    tags.push(`<meta name="twitter:image" content="${escapeHtmlAttribute(meta.ogImage)}" />`);

    if (meta.ogImageAlt) {
      tags.push(`<meta property="og:image:alt" content="${escapeHtmlAttribute(meta.ogImageAlt)}" />`);
      tags.push(`<meta name="twitter:image:alt" content="${escapeHtmlAttribute(meta.ogImageAlt)}" />`);
    }
  }

  return tags.join('\n    ');
}
